import React, {
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import moment from "moment";
import Link from "./link/Link";
import Logo from "./logo/Logo";
import stylez from "./header.module.scss";
import UserProfile from "./user-profile/UserProfile";
import RolePermission from "../role-permission/RolePermission";
import { UserRoleEnum } from "../../types";
import { useAnalytics, Events, useUser, useToast } from "@hooks";
import { useRolesPermissions } from "../../hooks/useRolesPermission";
import ParentUserProfile from "./parent-user-profile/ParentUserProfile";
import { useLoginMutation } from "../../hooks/useLoginMutation";
import OfficeHourModal from "./OfficeHourModal";
import TranslationBadge from "../translation-provider/TranslationBadge";
import {
  useCalendarEventsLazyQuery,
  useListLessonsInProgressLazyQuery,
  useResetAllLessonProgressMutation,
} from "../../api/graphql-codegen";
import UnitProgressModal from "@components/unit-progress-modal/UnitProgressModal";
import LessonProgressModal from "@src/components/lesson-progress-modal/LessonProgressModal";
import SchoolNewYearModal from "@src/pages/lesson-progress/school-new-year/SchoolNewYearModal";
import {
  isHomeEDApp,
  IntercomCheckListID,
  GroupOnboardingId,
  ProgramID,
  BookStudyUnitName,
} from "@constants";
import type { MenuProps } from "antd";
import "./customStyle.css";
import { MenuOutlined } from "@ant-design/icons";
import Button from "../button/Button";
import { Menu, Tooltip } from "antd";
import CloseIcon from "../../assets/icons/close.svg?react";
import MenuContent from "./MenuContent";
import TSFLogo from "@src/assets/icons/TSF_logo.svg?react";
import { LordIcon } from "../lord-icon/lord-icon";
import { useIntercom } from "react-use-intercom";
import IconOutline from "src/assets/icons/new-outline-info.svg?react";
import IconMouse from "./assets/mouse.svg?react";
import { InviteSlidingPanel } from "@src/pages/admin/users/invite-slide/inviteSlidePanel";
import { InviteSlidingPanel as SAInviteSlidePanel } from "@src/pages/super-admin/users/invite-slide/inviteSlidePanel";
import Spinner from "../spinner/Spinner";
import ResetModal from "@src/pages/lesson-progress/reset-modal/ResetModal";
import { useAuthToken } from "@src/hooks/useAuthToken";

import {
  WellBeingUnitId,
  EducatorOnboardingId,
  PlatformTowerId,
  StudentWellBeingSurveyId,
} from "@src/constants";
import GradeSelectModal from "../grade-select-modal/GradeSelectModal";
import ScheduleSupportCallModal from "../schedule-support-call-modal/ScheduleSupportCallModal";

const colors = { primary: "#FFB073", secondary: "#6C969A" };

type HeaderProps = {
  isParentApp?: boolean;
  inviteUser?: () => void;
  bulkInvite?: () => void;
  maintain?: boolean;
  isHidden?: boolean;
  isAussie?: boolean;
  reLoadData?: () => void;
};

const menuText = {
  courses: "Courses",
  accounts: "Accounts",
  lessons: "Lessons",
  invite: "Invite Staff",
  admin: "Admin",
  superAdmin: "Super Admin",
  bulkInviteText: "Bulk Invite",
  homeEd: "Home Ed",
  HomeEdUsers: "HE Users",
  HomeEdLinks: "HE Links",
  staff: "Staff",
  manageStaff: "Manage Staff",
  dashboard: "Dashboard",
  help: "Help",
  helpCentre: "Help Centre",
  profile: "Profile",
  logout: "Logout",
  community: "Community",
  groups: "Groups",
  units: "Units",
  hours: "Office Hours",
  wellBeing: "Educator Wellbeing",
  exitTicketReport: "Exit Ticket Reports",
  surveysExport: "Surveys Export",
  copyRegistrationLink: "Registration Link",
  reports: "REPORTS",
  opSA: "OP SA",
  myCourse: "My Courses",
};

function Header({
  isParentApp,
  maintain = false,
  isHidden = false,
  reLoadData,
}: HeaderProps) {
  const { pathname } = useLocation();
  const history = useHistory();
  const { successToast } = useToast();
  const [user] = useUser();
  const { showFirstResetLesson, setShowFirstResetLesson } = useAuthToken();
  const [getCalendarEvent] = useCalendarEventsLazyQuery({
    fetchPolicy: "network-only",
  });
  const timer = useRef<NodeJS.Timeout | null>(null);
  // type of user?
  // const isAdminOrSuper = useRolesPermissions([UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]);
  const isSuperAdmin = useRolesPermissions([UserRoleEnum.SuperAdmin]);
  const isContentAdmin = useRolesPermissions([UserRoleEnum.ContentAdmin]);
  const isAdmin = useRolesPermissions([
    UserRoleEnum.Admin,
    UserRoleEnum.DistrictAdmin,
  ]);
  const isAdminOrUser = useRolesPermissions([
    UserRoleEnum.Admin,
    UserRoleEnum.DistrictAdmin,
    UserRoleEnum.User,
  ]);
  const isJustUser = useMemo(
    () => !isAdmin && !isSuperAdmin,
    [isAdmin, isSuperAdmin],
  );
  const isDistrictAdmin = useRolesPermissions([UserRoleEnum.DistrictAdmin]);
  const isParentAppCheck = isParentApp || pathname.startsWith("/parent");
  const [, logout] = useLoginMutation(!!isParentAppCheck);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [inMeeting, setInMeeting] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);

  const handleCloseModal = useCallback(() => {
    setShowUnitModal(false);
    setIsPostProgram(false);
    setAssessments(false);
  }, []);

  const isAussie = useMemo(() => user?.id?.startsWith("AU_"), [user?.id]);
  const isNorthAmerica = useMemo(
    () =>
      !!user?.id &&
      (user.id.toLowerCase().startsWith("us") ||
        user.id.toLowerCase().startsWith("ca")),
    [user?.id],
  );

  const [mobileWidth, styles] = useMemo(() => {
    let mobileWidth = 768;
    let styles = stylez;

    // set custom sizes and stylesheet for different users
    // if (isParentAppCheck) {
    //   mobileWidth = 915;
    // } else if (isSuperAdmin) {
    //   mobileWidth = 800;
    //   // styles = stylezSuperAdmin;
    // } else if (isAdmin) {
    //   mobileWidth = 600;
    // } else if (isJustUser) {
    //   mobileWidth = 600;
    // }
    return [mobileWidth, styles];
  }, [isAdmin, isJustUser, isParentAppCheck, isSuperAdmin]);

  // isMobile
  const isMobile = useMediaQuery({ maxWidth: mobileWidth });

  // Determine if sidebar should be fixed (dashboard page, but not on mobile)
  const isSidebarFixed = useMemo(() => {
    if (isMobile) return false; // Always use slide-in on mobile
    return pathname === "/dashboard" || pathname.startsWith("/dashboard/");
  }, [pathname, isMobile]);
  const isDashboardPage = useMemo(() => {
    return pathname === "/dashboard" || pathname.startsWith("/dashboard/");
  }, [pathname]);
  const isPhone = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const regexs = [
      /(Android)(.+)(Mobile)/i,
      /BlackBerry/i,
      /iPhone|iPod/i,
      /Opera Mini/i,
      /IEMobile/i,
    ];
    return regexs.some((b) => userAgent.match(b));
  }, []);

  const screenWidth = -412;

  const { trackEvent } = useAnalytics();
  const [isMenuOpen, toggleMenu] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);

  const [showMenuView, setShowMenuView] = useState(false);

  const { show, isOpen } = useIntercom();

  const [isDashboard, setIsDashboard] = useState(false);
  const [isUserManagement, setIsUserManagement] = useState(false);
  const [isSuperAdminTools, setIsSuperAdminTools] = useState(false);
  const [isContentAdminTools, setIsContentAdminTools] = useState(false);
  const [isHomeEdTools, setIsHomeEdTools] = useState(false);
  const [isOnboardingGuides, setIsOnboardingGuides] = useState(false);
  const [isGettingStarted, setIsGettingStarted] = useState(false);
  const [isstudyGuidesAssessmentReports, setIsStudyGuidesAssessmentReports] =
    useState(false);
  const [isClassroomLessons, setIsClassroomLessons] = useState(false);
  const [isEducatorCaregiverResources, setIsEducatorCaregiverResources] =
    useState(false);
  const [isSupport, setIsSupport] = useState(false);
  const [isMyAccount, setIsMyAccount] = useState(false);

  const [isManagement, setIsManagement] = useState(false);
  const [isInviteStaff, setIsInviteStaff] = useState(false);
  const [isAccount, setIsAccount] = useState(false);
  const [isUsageInsight, setIsUsageInsight] = useState(false);
  const [isCourses, setIsCourses] = useState(false);
  const [isUnits, setIsUnits] = useState(false);
  const [isLessons, setIsLessons] = useState(false);
  const [isGroups, setIsGroups] = useState(false);
  const [isCollections, setIsCollections] = useState(false);
  const [isMultiCollections, setIsMultiCollections] = useState(false);
  const [isPrograms, setIsPrograms] = useState(false);
  const [isHeUsers, setIsHeUsers] = useState(false);
  const [isHeLinks, setIsHeLinks] = useState(false);

  const [isResourceHub, setIsResourceHub] = useState(false);
  const [isOnboardingHandbook, setIsOnboardingHandbook] = useState(false);
  const [isCaregiverIntroduction, setIsCaregiverIntroduction] = useState(false);
  const [isGroupOnboarding, setIsGroupOnboarding] = useState(false);
  const [isKickOffCall, setIsKickOffCall] = useState(false);
  const [isScopeSequence, setIsScopeSequence] = useState(false);
  const [isEducatorOnboarding, setIsEducatorOnboarding] = useState(false);
  const [isPlatformTour, setIsPlatformTour] = useState(false);
  const [isGettingStartedGuide, setIsGettingStartedGuide] = useState(false);
  const [isSummaryReports, setIsSummaryReports] = useState(false);
  const [isAsseessmentsStudyGuides, setIsAsseessmentsStudyGuides] =
    useState(false);
  const [isStudentWellbeing, setIsStudentWellbeing] = useState(false);
  const [isStudentIntroduction, setIsStudentIntroduction] = useState(false);
  const [isBrainBreaks, setIsBrainBreaks] = useState(false);
  const [isStudentPreProgramQuiz, setIsStudentPreProgramQuiz] = useState(false);
  const [isStudentPostProgramQuiz, setIsStudentPostProgramQuiz] =
    useState(false);
  const [isPostProgram, setIsPostProgram] = useState(false);
  const [isMyCollections, setIsMyCollections] = useState(false);
  const [isStandaloneLessons, setIsStandaloneLessons] = useState(false);
  const [isMultiLessonUnits, setIsMultiLessonUnits] = useState(false);
  const [isEducatorWellbeing, setIsEducatorWellbeing] = useState(false);
  const [isCreatingResilient, setIsCreatingResilient] = useState(false);
  const [isCaregiverResources, setIsCaregiverResources] = useState(false);
  const [isBookStudy, setIsBookStudy] = useState(false);
  const [isHelpCentre, setIsHelpCentre] = useState(false);
  const [isLiveChat, setIsLiveChat] = useState(false);
  const [isViewActiveLessons, setIsViewActiveLessons] = useState(false);
  const [isMyProfile, setIsMyProfile] = useState(false);
  const [isPrivacyPolicy, setIsPrivacyPolicy] = useState(false);
  const [isTermsofUse, setIsTermsofUse] = useState(false);

  const [isWellBeingFoundation, setIsWellBeingFoundation] = useState(false);
  const [isSkillBuilder, setIsSkillBuilder] = useState(false);

  const [isOffice, setIsOffice] = useState(false);
  const [isLogout, setIsLogout] = useState(false);
  const [inviteSlide, setInviteSlide] = useState(false);
  const [scrollTopPos, setScrollTopPos] = useState(0);
  const [showDownButtom, setShowDownButton] = useState(false);
  const [resetAllLesson, setResetAllLesson] = useState(false);

  const [unitId, setUnitId] = useState("");
  const [assessments, setAssessments] = useState(false);
  const [lessonId, setLessonId] = useState("");
  const [showPlatformTower, setShowPlatformTower] = useState(false);
  const [showAusPreProgram, setShowAusPreProgram] = useState(false);
  const [showSelectGradeModal, setShowSelectGradeModal] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState("");
  const [showSchoolNewYearModal, setShowSchoolNewYearModal] = useState(false);
  const schoolNewYearModalCheckedThisSessionRef = useRef(false);
  const [isBookCrows, setIsBookCrows] = useState(false);
  const [isSupporting, setIsSupporting] = useState(false);
  const [showScheduleSupportCall, setShowScheduleSupportCall] = useState(false);

  const isTomAccount = useMemo(() => {
    if (user) {
      return user?.email === "tom@openparachuteschools.com" ||
        user?.email === "robert@rekordio.com" ||
        user?.email === "robert+superadmin@rekordio.com"
        ? true
        : false;
    }
    return false;
  }, [user]);

  const isTrial = useMemo(
    () => user?.customer?.trial ?? false,
    [user?.customer],
  );
  const isSouthAU = useMemo(
    () => (user?.customer?.state === "South Australia" ? true : false),
    [user?.customer],
  );

  // School New Year modal:
  // - AU/NA only, at school-year start (Jan+ AU, Jul+ NA)
  // - once per user per school year and once per login session
  // - existing users only (at least one completed lesson)
  // Session ref is set before the count check so completing a lesson mid-session
  // (e.g. generating an exit ticket) cannot open the modal later in the same login.
  useEffect(() => {
    if (!user || !(isAussie || isNorthAmerica)) return;
    if (schoolNewYearModalCheckedThisSessionRef.current) return;
    schoolNewYearModalCheckedThisSessionRef.current = true;
    if ((user.completedLessonCount ?? 0) <= 0) return;

    const now = moment();
    const year = now.year();
    const month = now.month(); // 0 = Jan, 6 = Jul
    const isInSchoolYearStartWindow = isAussie
      ? month >= 0 // January onwards for Australia
      : month >= 6; // July 1 onwards for North America
    if (!isInSchoolYearStartWindow) return;

    const schoolYearKey = isAussie
      ? String(year)
      : `${month >= 6 ? year : year - 1}-${month >= 6 ? year + 1 : year}`;
    let alreadySeenThisYear = false;
    if (user.schoolNewYearInfo) {
      try {
        const schoolNewYearInfo = JSON.parse(user.schoolNewYearInfo) as Record<
          string,
          boolean
        >;
        alreadySeenThisYear = !!schoolNewYearInfo[schoolYearKey];
      } catch {
        // invalid JSON: treat as not seen
      }
    }
    if (!alreadySeenThisYear) {
      setShowSchoolNewYearModal(true);
    }
  }, [user, isAussie, isNorthAmerica]);

  const schoolYearKey = useMemo(() => {
    const now = moment();
    const y = now.year();
    const m = now.month();
    return isAussie ? String(y) : m >= 6 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
  }, [isAussie]);

  const isTSFAussie = useMemo(() => {
    const groups = user?.schools?.[0]?.groupIds?.split(",");
    return groups?.includes("7");
  }, [user]);

  const isAUuser = useMemo(() => {
    return user?.id?.toLowerCase().startsWith("au");
  }, [user?.id]);
  const isCAuser = useMemo(() => {
    return user?.id?.toLowerCase().startsWith("ca");
  }, [user?.id]);
  const isUSuser = useMemo(() => {
    return user?.id?.toLowerCase().startsWith("us");
  }, [user?.id]);
  const isPrimary = useMemo(() => {
    return user?.schools?.[0]?.isPrimary ?? false;
  }, [user?.schools]);

  // valid accounts when created before August 1, 2023.
  const validUser = useMemo(() => {
    if (user?.createdAt) {
      const baseDate = new Date("August 1, 2023").getTime();
      return (
        baseDate - parseInt(user.createdAt) > 0 &&
        !showFirstResetLesson &&
        !pathname.startsWith("/lessonprogress")
      );
    }
    return false;
  }, [user?.createdAt, showFirstResetLesson, pathname]);

  const [listLessonsInProgress, { data, loading, refetch }] =
    useListLessonsInProgressLazyQuery({ fetchPolicy: "network-only" });
  const [resetAllLessonProgress, { loading: deleting }] =
    useResetAllLessonProgressMutation({
      onCompleted(res) {
        if (res.resetAllLessonProgress) {
          successToast(`Lesson Progress has reset Successfully!`);
          refetch();
        }
        setResetAllLesson(false);
      },
      onError(e) {
        successToast(e.message);
      },
    });

  useEffect(() => {
    if (!isAUuser) {
      // listLessonsInProgress();
    }
  }, [listLessonsInProgress, isAUuser]);

  const isHasLessonLibrary = useMemo(() => {
    if (user?.id?.startsWith("AU_")) {
      if (user?.schools?.[0]?.programs?.includes("CvsCvxSsD-E0Vv9ea1q1i")) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  }, [user?.id, user?.schools?.[0]?.programs]);

  const lessonProgressBadge = useMemo(() => {
    return data?.listLessonsInProgress?.length ?? 0;
  }, [data?.listLessonsInProgress]);

  const handleResetAllProgress = useCallback(() => {
    resetAllLessonProgress();
  }, [resetAllLessonProgress]);

  const handleOpenResetModal = () => {
    setShowFirstResetLesson("Yes");
    setResetAllLesson(true);
  };

  const gotoLessonProgress = useCallback(() => {
    setShowFirstResetLesson("Yes");
    setResetAllLesson(false);
    history.replace("/lessonprogress");
  }, [setShowFirstResetLesson, history]);

  const handleOfficeHours = (e?: any) => {
    e && e.preventDefault();
    if (isMenuOpen) toggleMenu(false);
    setShowSupportModal(true);
  };

  const checkIfInMeeting = useCallback(async () => {
    const data = (await getCalendarEvent()).data?.calendarEvents;
    const isInMeeting =
      !!data?.clinical.isInMeeting || !!data?.onboarding.isInMeeting;
    const timeLeft = Math.min(
      data?.clinical.timeLeftInSec || 0,
      data?.onboarding.timeLeftInSec || 0,
    );
    if (isInMeeting !== inMeeting) {
      setInMeeting(isInMeeting);
    }
    if (timeLeft > 0) {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(
        async () => {
          const data = (await getCalendarEvent()).data?.calendarEvents;
          const isInMeeting =
            !!data?.clinical.isInMeeting || !!data?.onboarding.isInMeeting;
          if (isInMeeting !== inMeeting) {
            setInMeeting(isInMeeting);
          }
        },
        (timeLeft + 1) * 1000,
      );
    } else if (isInMeeting && timeLeft < 0) {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(
        async () => {
          const data = (await getCalendarEvent()).data?.calendarEvents;
          const isInMeeting =
            !!data?.clinical.isInMeeting || !!data?.onboarding.isInMeeting;
          if (isInMeeting !== inMeeting) {
            setInMeeting(isInMeeting);
          }
        },
        (timeLeft + 31 * 60) * 1000,
      );
    }
  }, [getCalendarEvent, inMeeting]);

  useEffect(() => {
    if (isHomeEDApp) {
      return;
    }
    // const intervalHandler = setInterval(checkIfInMeeting, FetchingInterval * 1000);
    checkIfInMeeting();
    // return () => {
    //   if (intervalHandler) {
    //     clearInterval(intervalHandler);
    //   }
    // }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [checkIfInMeeting]);

  const VertDivider = () => <div className={styles.headerDivider} />;

  const closeSupportModal = () => {
    setShowSupportModal(false);
  };

  const inviteUserHandler = useCallback(() => {
    setShowMenuView(false);
    setInviteSlide(true);
  }, []);

  const openUsagePage = useCallback(() => {
    if (isSuperAdmin) {
      history.push("/super/usage");
    } else if (isAdmin || isDistrictAdmin) {
      history.push("/admin/usage");
    } else return;
  }, [history, isSuperAdmin, isAdmin, isDistrictAdmin]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "auto";
    document.getElementById("root")!.style.overflow = isMenuOpen
      ? "hidden"
      : "auto";
  }, [isMenuOpen]);

  const handleScroll = () => {
    const elementMenu = document.getElementById("menuList");
    if (elementMenu) {
      const scrollTop = elementMenu.scrollTop;
      const isNearBottom =
        elementMenu.scrollTop + elementMenu.clientHeight <
        elementMenu.scrollHeight;

      setScrollTopPos(scrollTop);
      setShowDownButton(isNearBottom);
    }
  };

  useEffect(() => {
    const elementMenu = document.getElementById("menuList");
    if (elementMenu) {
      const scrollTop = elementMenu.scrollTop;
      const isNearBottom =
        elementMenu.scrollTop + elementMenu.clientHeight <
        elementMenu.scrollHeight;

      setScrollTopPos(scrollTop);
      setShowDownButton(isNearBottom);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollUp = useCallback(() => {
    const elementMenu = document.getElementById("menuList");
    if (elementMenu) {
      elementMenu.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  const handleScrollDown = useCallback(() => {
    const elementMenu = document.getElementById("endMenu");
    if (elementMenu) {
      elementMenu.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, []);

  const closeOverlay = () => {
    toggleMenu(false);
  };

  const openWellBeingUnitModal = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      setShowMenuView(false);
      setUnitId(WellBeingUnitId);
      setShowUnitModal(true);
      trackEvent(Events.actions.educatorWellbeingUnitOpen, {
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [trackEvent, user],
  );

  const openProfessionalDevelopment = useCallback(() => {
    if (isAussie) {
      history.push(
        `/program/professionaldevelopment?pid=${ProgramID.AUPROFESSIONALDEV}`,
      );
    } else {
      history.push(
        `/program/professionaldevelopment?pid=${ProgramID.PROFESSIONALDEV}`,
      );
    }
  }, [history, isAussie]);

  const openWellbeingFoundations = useCallback(
    (tab: string) => {
      if (pathname.startsWith("/wellbeingfoundations")) {
        onMenuClose();
      }
      history.replace(`/wellbeingfoundations?tab=${tab}`);
    },
    [history, pathname],
  );

  const openCaregiver = useCallback(() => {
    if (pathname.startsWith("/caregiver")) {
      onMenuClose();
    }
    history.replace("/caregiver");
  }, [history, pathname]);

  const openCurriculumMapping = () => {
    if (isAussie) {
      window.open(
        "https://help.openparachuteschools.com/en/articles/6949377-curriculum-mapping",
        "_blank",
      );
      return false;
    }
  };

  const openBookResilience = () => {
    if (isAussie) {
      window.open(
        "https://calendly.com/adelaidecrows-foundation/open-parachute-wellbeing-visit?month=2025-08",
        "_blank",
      );
      return false;
    }
  };

  const openHandbook = () => {
    setShowMenuView(false);
    if (isAUuser) {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Open+Parachute+-+Onboarding+Handbook+AU.pdf",
        "_blank",
      );
      return false;
    } else {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Onboarding+Handbook+OP.pdf",
        "_blank",
      );
      return false;
    }
  };

  const openEducatorOnboardingProgram = useCallback(() => {
    history.push(
      `/program/educatoronboarding?pid=${ProgramID.EDUCATORONBOARDING}`,
    );
  }, [history]);

  const openBookStudy = useCallback(() => {
    history.push(`/${BookStudyUnitName}`);
  }, [history]);

  const openSupportingStudentBook = () => {
    window.open(
      "https://www2.gov.bc.ca/assets/gov/erase/documents/mental-health-wellness/supporting-students-in-times-of-change.pdf",
      "_blank",
    );
  };

  const openSchoolLeaderOnboarding = () => {
    if (isTrial || isAussie) {
      return;
    }

    window.open(
      "https://learn.openparachuteschools.com/standalone/school-leader-onboarding",
      "_blank",
    );
  };

  const openDistrictLeaderOnboarding = () => {
    if (isTrial || isAussie) {
      return;
    }

    window.open(
      "https://learn.openparachuteschools.com/standalone/district-leader-onboarding",
      "_blank",
    );
  };

  const openCaregiverIntroduction = () => {
    setShowMenuView(false);
    if (isUSuser) {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Open+Parachute+Wellbeing+Resources+for+Caregivers+US.pdf",
        "_blank",
      );
      return false;
    }
    if (isCAuser) {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Open+Parachute+Wellbeing+Resources+for+Caregivers+CA.pdf",
        "_blank",
      );
      return false;
    }
    if (isAUuser) {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Open+Parachute+Wellbeing+Resources+AU.pdf",
        "_blank",
      );
      return false;
    }
  };

  const openGroupOnboardingUnit = useCallback(() => {
    setUnitId(GroupOnboardingId);
    setAssessments(false);
    setShowUnitModal(true);
  }, []);

  const openScheduleKickOffCall = () => {
    if (isTrial) {
      return;
    }
    if (isAussie) {
      window.open(
        "https://calendly.com/openparachute-rikki/kickoff-call",
        "_blank",
      );
      return false;
    } else {
      window.open(
        "https://calendly.com/madison-hilliard/30min?month=2025-07",
        "_blank",
      );
      return false;
    }
  };

  const openScopeSequece = () => {
    if (isAussie) return;
    window.open(
      "https://help.openparachuteschools.com/en/collections/9385363-scope-sequence",
      "_blank",
    );
  };

  const openEducatorOnboardingUnit = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowMenuView(false);
    window.open(
      "https://help.openparachuteschools.com/en/collections/10400596-quickstart-for-educators",
      "_blank",
    );
  }, []);

  const openPlatformTour = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowMenuView(false);
    setLessonId(EducatorOnboardingId);
    setShowPlatformTower(true);
  }, []);

  const intercomGettingStarted = () => {
    setShowMenuView(false);
    if (isAUuser) {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/getting-started-guide-au.pdf",
        "_blank",
      );
      return;
    } else {
      window.open(
        "https://help.openparachuteschools.com/en/collections/10703611-roadmaps",
        "_blank",
      );
      return;
    }
  };

  const openExitTicketSummary = useCallback(() => {
    setShowMenuView(false);
    if (isSuperAdmin) {
      history.push("/super/survey-exports");
    } else if (isAdminOrUser) {
      history.push("/summaryreports");
    }
    trackEvent(Events.views.exitTicketReports, {
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [history, isAdminOrUser, isSuperAdmin, user, trackEvent]);

  const openStudentWellBeingUnit = useCallback((e?: any) => {
    e.preventDefault();
    e.stopPropagation();

    setShowMenuView(false);
    setUnitId(StudentWellBeingSurveyId);
    setAssessments(false);
    setShowUnitModal(true);
  }, []);

  const openAssessmentsStudyguides = useCallback(() => {
    if (isCAuser || isUSuser) return;
    setShowMenuView(false);
    history.push("/assessments?tab=1");
  }, [history, isCAuser, isUSuser]);

  const openNAPrePromQuizUnit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      setShowMenuView(false);
      setUnitId("");
      setIsPostProgram(false);
      setAssessments(true);
      setShowUnitModal(true);

      trackEvent(Events.actions.preProgramQuizOpen, {
        unit_name: "Pre-Program Quiz",
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [user, trackEvent],
  );

  const openAUPrePromQuizUnit = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowMenuView(false);
    setUnitId("");
    setLessonId("");
    setShowSelectGradeModal(true);
  }, []);

  const openNAPostProgramQuizUnit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      setShowMenuView(false);
      setAssessments(false);
      setIsPostProgram(true);
      setUnitId("");
      setLessonId("");
      setShowUnitModal(true);

      trackEvent(Events.actions.preProgramQuizOpen, {
        unit_name: "Post-Program Quiz",
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [user, trackEvent],
  );

  const handleOpenAusPreProgramQuiz = useCallback(
    (programId: string) => {
      if (programId) {
        setSelectedGrade(programId);
        setShowAusPreProgram(true);
        setShowSelectGradeModal(false);
      }
      trackEvent(Events.actions.preProgramQuizOpen, {
        unit_name: "Pre-Program Quiz",
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [user, trackEvent],
  );

  const onMenuClose = () => {
    setShowMenuView(false);
  };

  const openMyCourses = () => {
    if (pathname.startsWith("/lessons")) {
      onMenuClose();
    }
    history.replace("/lessons?tab=programs");
  };

  const openTransitions = () => {
    if (pathname.startsWith("/program/transitions")) {
      onMenuClose();
    }
    history.push(`/program/transitions?pid=${ProgramID.AUSTRANSITIONS}`);
  };

  const openLessonLibrary = () => {
    if (pathname.startsWith("/lessons")) {
      onMenuClose();
    }
    history.replace("/lessons?tab=library");
  };

  const openIntercom = (e?: any) => {
    // e.preventDefault();
    // e.stopPropagation();
    onMenuClose();
    show();
  };

  const openLessonProgress = useCallback(() => {
    onMenuClose();
    history.push("/lessonprogress");
  }, [history]);

  const triggerIntercomChecklist = (e?: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    onMenuClose();
    if ((window as any).Intercom && isOpen) {
      (window as any).Intercom("startChecklist", IntercomCheckListID);
    }
  };

  // serve parent app menu first, for safety reasons
  if (isParentAppCheck) {
    return (
      <div className={`${styles.wrapper} ${isMenuOpen ? styles.openMenu : ""}`}>
        <div
          className={`${styles.darkOverlayHidden} ${styles.mobileOverlay}`}
          onClick={closeOverlay}
        />
        <div className={styles.logo}>
          <Logo isParentApp={isParentAppCheck} />
        </div>
        <div className={styles.links}>
          {!isPhone && (
            <Link to={"/parent/dashboard"}>{menuText.dashboard}</Link>
          )}
          {!isPhone && <VertDivider />}
          <ParentUserProfile isMobile={isPhone} menuText={menuText} />
        </div>
      </div>
    );
  }

  // New Slide Menu
  const userManageHandler = () => {
    setShowMenuView(false);
    if (isSuperAdmin) {
      history.push("/super/users");
    } else {
      history.push("/admin/users");
    }
  };

  const surveyExportsHandler = () => {
    setShowMenuView(false);
    history.push("/super/survey-exports");
  };

  const userProgramHandler = () => {
    setShowMenuView(false);
    history.push("/units/studentvoiceseries");
  };

  const goToDashboard = () => {
    setShowMenuView(false);
    history.push("/dashboard");
  };

  const openScheduleSupportCall = useCallback(() => {
    setShowMenuView(false);
    setShowScheduleSupportCall(true);
  }, []);

  return (
    <div
      className={
        isHidden ? styles.headerContainerHidden : styles.headerContainer
      }
    >
      <div className={`${styles.wrapper} ${isMenuOpen ? styles.openMenu : ""}`}>
        <div
          className={`${styles.darkOverlayHidden} ${styles.mobileOverlay}`}
        />

        <div className={styles.leftmenu}>
          {!isSidebarFixed && (
            <Button
              label=""
              className={styles.menuButton}
              leftIcon={<MenuOutlined className={styles.burgerIcon} />}
              onClick={() => {
                setShowMenuView(true);
              }}
            />
          )}
          <div className={styles.logo}>
            <Logo isParentApp={isParentAppCheck} />
          </div>
          {isTSFAussie && (
            <div className={styles.tsfLogo}>
              <TSFLogo />
            </div>
          )}
        </div>

        {maintain ? (
          <>
            <Link href={"https://help.openparachuteschools.com/"}>
              {"SUPPORT"}
            </Link>
          </>
        ) : (
          <>
            {/*OP Super Admin menu is here*/}
            {(isSuperAdmin || isContentAdmin) && (
              <RolePermission
                permissions={[
                  UserRoleEnum.SuperAdmin,
                  UserRoleEnum.ContentAdmin,
                ]}
              >
                <div className={styles.links}>
                  <>
                    {lessonProgressBadge > 0 && validUser && (
                      <>
                        <div className={styles.lessonNotify}>
                          <span>{"Action Required!"}</span>
                          <h1>{"Reset Lesson Progress"}</h1>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                          onClick={handleOpenResetModal}
                        >
                          <div className={styles.progressBadgeView}>
                            {loading ? (
                              <Spinner width={20} height={20} />
                            ) : (
                              <>
                                {lessonProgressBadge > 0 && (
                                  <div className={styles.whiteBadge}>
                                    {lessonProgressBadge}
                                  </div>
                                )}
                                <span>..</span>
                              </>
                            )}
                          </div>
                        </div>
                        <VertDivider />
                      </>
                    )}

                    <VertDivider />
                    <div className={styles.schoolName}>
                      {user?.schools?.[0]?.name}
                    </div>
                    <VertDivider />

                    <TranslationBadge type="navbar" />
                    <VertDivider />

                    <UserProfile
                      mobileWidth={mobileWidth}
                      menuText={menuText}
                      isHidden={isHidden}
                    />
                    {isMobile ? (
                      <>
                        <Link to={"/profile"}>{menuText.profile}</Link>
                        <Link href="#" onClick={logout}>
                          {menuText.logout}
                        </Link>
                      </>
                    ) : null}
                  </>
                </div>
              </RolePermission>
            )}
            {/*OP Super Admin menu ends here*/}

            {/*Admin menu is here*/}
            {/*<RolePermission permissions={[UserRoleEnum.SuperAdmin]}>*/}
            {isAdmin && !isSuperAdmin && (
              <RolePermission
                permissions={[UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin]}
              >
                <div className={styles.links}>
                  {lessonProgressBadge > 0 && validUser && (
                    <>
                      <div className={styles.lessonNotify}>
                        <span>{"Action Required!"}</span>
                        <h1>{"Reset Lesson Progress"}</h1>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        onClick={handleOpenResetModal}
                      >
                        <div className={styles.progressBadgeView}>
                          {loading ? (
                            <Spinner width={20} height={20} />
                          ) : (
                            <>
                              {lessonProgressBadge > 0 && (
                                <div className={styles.whiteBadge}>
                                  {lessonProgressBadge}
                                </div>
                              )}
                              <span>..</span>
                            </>
                          )}
                        </div>
                      </div>
                      <VertDivider />
                    </>
                  )}

                  <VertDivider />
                  <div className={styles.schoolName}>
                    {user?.schools?.[0]?.name}
                  </div>
                  <VertDivider />

                  {/* <Link
                    to={"#"}
                    onClick={() => {
                      setShowMenuView(true);
                    }}
                  >
                    {"Menu"}
                  </Link>
                  <VertDivider /> */}

                  <TranslationBadge />
                  <VertDivider />

                  <UserProfile
                    mobileWidth={mobileWidth}
                    menuText={menuText}
                    isHidden={isHidden}
                  />
                  {isMobile && (
                    <>
                      <Link to={"/profile"}>{menuText.profile}</Link>
                      <Link href="#" onClick={logout}>
                        {menuText.logout}
                      </Link>
                    </>
                  )}
                </div>
              </RolePermission>
            )}
            {/*Admin menu ends here*/}

            {/*User menu is here*/}
            {isJustUser && (
              <RolePermission permissions={[UserRoleEnum.User]}>
                {/*<RolePermission permissions={[UserRoleEnum.SuperAdmin]}>*/}
                <div className={styles.links}>
                  <VertDivider />
                  <div className={styles.schoolName}>
                    {user?.schools?.[0]?.name}
                  </div>
                  <VertDivider />

                  {/* <Link
                    to={"#"}
                    onClick={() => {
                      setShowMenuView(true);
                    }}
                  >
                    {"Menu"}
                  </Link>
                  <VertDivider /> */}

                  <TranslationBadge type="navbar" />
                  <VertDivider />

                  <UserProfile mobileWidth={mobileWidth} menuText={menuText} />
                  {isMobile ? (
                    <>
                      <Link to={"/profile"}>{menuText.profile}</Link>
                      <Link href="#" onClick={logout}>
                        {menuText.logout}
                      </Link>
                    </>
                  ) : null}
                </div>
              </RolePermission>
            )}
          </>
        )}
        {/*User menu ends here*/}
      </div>

      {showSupportModal && (
        <OfficeHourModal open={showSupportModal} onClose={closeSupportModal} />
      )}

      {showUnitModal && (
        <UnitProgressModal
          open={showUnitModal}
          onClose={handleCloseModal}
          unitId={unitId}
          assessments={assessments}
          isPostProgram={isPostProgram}
        />
      )}

      {showPlatformTower && lessonId && (
        <LessonProgressModal
          open={showPlatformTower}
          onClose={() => setShowPlatformTower(false)}
          lessonId={PlatformTowerId}
          isPlatformTower={true}
        />
      )}

      {showSchoolNewYearModal && (
        <SchoolNewYearModal
          open={showSchoolNewYearModal}
          schoolYearKey={schoolYearKey}
          onClose={() => {
            setShowSchoolNewYearModal(false);
            history.push("/dashboard");
          }}
          onOkAction={() => {
            setShowSchoolNewYearModal(false);
            history.push("/lessonprogress");
          }}
        />
      )}

      {showAusPreProgram && (
        <LessonProgressModal
          open={showAusPreProgram}
          onClose={() => setShowAusPreProgram(false)}
          lessonId={lessonId}
          isAusPreProgramQuiz={showAusPreProgram}
          gradeName={selectedGrade}
        />
      )}

      {showSelectGradeModal && (
        <GradeSelectModal
          open={showSelectGradeModal}
          onClose={() => setShowSelectGradeModal(false)}
          onContinue={handleOpenAusPreProgramQuiz}
        />
      )}

      <ScheduleSupportCallModal
        open={showScheduleSupportCall}
        onClose={() => setShowScheduleSupportCall(false)}
      />

      {resetAllLesson && (
        <ResetModal
          title={
            "Would you like to clear your Completed Lessons from last year?"
          }
          description={
            "View your Lesson Progress to clear all In Progress and Completed lessons from last year "
          }
          resetTitle={"Reset Lesson Status"}
          cancelTitle="Cancel"
          reviewLessonButton={"View Lesson Progress"}
          open={resetAllLesson}
          onClose={() => setResetAllLesson(false)}
          onReset={handleResetAllProgress}
          loading={deleting}
          goToResetLesson={gotoLessonProgress}
        />
      )}

      {(isAdmin || isDistrictAdmin) && inviteSlide && (
        <InviteSlidingPanel
          bulkInviteSlide={inviteSlide}
          setBulkInviteSlide={setInviteSlide}
          reLoadData={reLoadData}
        />
      )}

      {isSuperAdmin && inviteSlide && (
        <SAInviteSlidePanel
          bulkInviteSlide={inviteSlide}
          setBulkInviteSlide={setInviteSlide}
          reLoadData={reLoadData}
        />
      )}

      {!isSidebarFixed && showMenuView && (
        <>
          <div
            className={styles.backDrop}
            onClick={() => setShowMenuView(false)}
          />
          <button onClick={onMenuClose} className={styles.closeWrapper}>
            <CloseIcon className={styles.closeIcon} />
          </button>
        </>
      )}

      {isSidebarFixed ? (
        <div className={styles.fixedSidebar}>
          <MenuContent
            isAussie={isAussie}
            isHasLessonLibrary={isHasLessonLibrary}
            setShowMenuView={setShowMenuView}
            showCloseButton={false}
            hideDashboardMenuItem={isDashboardPage}
            isParentAppCheck={isParentAppCheck}
            onCloseMenu={onMenuClose}
            inviteUserHandler={inviteUserHandler}
            openStudentWellBeingUnit={openStudentWellBeingUnit}
            openExitTicketSummary={openExitTicketSummary}
            openIntercom={openIntercom}
            openScheduleSupportCall={openScheduleSupportCall}
            logout={logout}
            isAUuser={isAUuser}
          />
        </div>
      ) : (
        <div
          className={styles.mainMenuList}
          style={{
            transform: showMenuView
              ? "translateX(0)"
              : `translateX(${screenWidth}px)`,
          }}
        >
          <MenuContent
            isAussie={isAussie}
            isHasLessonLibrary={isHasLessonLibrary}
            setShowMenuView={setShowMenuView}
            showCloseButton={true}
            hideDashboardMenuItem={isDashboardPage}
            isParentAppCheck={isParentAppCheck}
            onCloseMenu={onMenuClose}
            inviteUserHandler={inviteUserHandler}
            openStudentWellBeingUnit={openStudentWellBeingUnit}
            openExitTicketSummary={openExitTicketSummary}
            openIntercom={openIntercom}
            openScheduleSupportCall={openScheduleSupportCall}
            logout={logout}
            isAUuser={isAUuser}
          />
        </div>
      )}
    </div>
  );
}

export default Header;
